import { ServerError, ServerParseError } from '@apollo/client';
import { Sentry } from '@opendoor/observability';
import { GraphQLError } from 'graphql';

import { APIError } from '.';
import { GetSellerInputQuery_reception_getSellerInput_sellerInput } from './queries/__generated__/GetSellerInputQuery';

type SellerInput = Omit<
  GetSellerInputQuery_reception_getSellerInput_sellerInput,
  'featureFlagsAmplitude'
>;

export const maybeHandleGraphQLError = (
  errors: ReadonlyArray<GraphQLError> | undefined,
  networkError?: Error | ServerParseError | ServerError | null,
  options?: { operation: string; ignoreGraphQLErrors?: boolean },
) => {
  // gRPC codes that are user initiated.
  const userInitiatedErrorCodes: Array<number> = [3 /* InvalidArgument */];
  if (errors === undefined) {
    return;
  }

  let userError = false;
  for (const err of errors) {
    userError = userInitiatedErrorCodes.includes(err?.extensions?.exception?.code);
    if (!userError && !options?.ignoreGraphQLErrors) {
      // Don't break, because let's log every non-user error
      Sentry.captureException(err);
    }
  }

  const hasNetworkError = networkError != null;
  const hasBlockingGraphQLError = errors && !options?.ignoreGraphQLErrors;

  if (!hasNetworkError && !hasBlockingGraphQLError) {
    return;
  }

  const errMsg = hasNetworkError
    ? `Network error on GraphQL operation: ${options?.operation}`
    : userError
    ? `Error(s) have occurred. Please check your input again`
    : `GraphQL error(s) on GraphQL operation: ${options?.operation}`;
  throw new APIError(!hasNetworkError, errMsg);
};

export function validateSellerInput(
  sellerInput: SellerInput | null | undefined,
  { fatal, errMsg, extras }: { errMsg: string; fatal?: boolean; extras?: Record<string, unknown> },
): asserts sellerInput is NonNullable<SellerInput> {
  if (!sellerInput) {
    Sentry.withScope((scope) => {
      extras && scope.setExtras(extras);
      Sentry.captureException(errMsg);
    });

    throw new APIError(fatal, errMsg);
  }
}
